/**
 * The register list's contact column: website on top, one contact under it.
 *
 * This column exists because the search box can now match an e-mail or a phone
 * number. Without it an operator pastes a number, gets one row back, and has no
 * way to see which field made it match - which is indistinguishable from a bug.
 *
 * Its own file rather than another cell inside `AriregisterList.tsx`, which was
 * already at 372 lines (AGENTS.md §4).
 */
import { Globe, Mail } from "lucide-react";
import type { ArListRow } from "../../../lib/ariregister";
import { parseWebUrl } from "../../../lib/webUrl";
import { PhoneLink } from "../../telephony/PhoneLink";

/** Digits and telephone punctuation only - the same shape the server gates the
 *  phone branch on, but used here for a DISPLAY preference and nothing else. */
const LOOKS_NUMERIC = /^[+\d(][\d\s\-.()/]*$/;

/**
 * Which of the two contacts to put on the second line.
 *
 * A display preference, deliberately NOT the server's predicate. The server may
 * match a row on either column; this only decides which one is worth the single
 * line the layout has. Keeping it separate means a drift here is a cosmetic
 * annoyance rather than a correctness bug - see `lib/audience/queryMatch.ts` in
 * the API for the real rule.
 */
export function preferredContact(row: ArListRow, q: string): { kind: "phone" | "email"; value: string } | null {
  const typedANumber = q.trim() !== "" && LOOKS_NUMERIC.test(q.trim());
  if (typedANumber && row.phone) return { kind: "phone", value: row.phone };
  if (row.email) return { kind: "email", value: row.email };
  return row.phone ? { kind: "phone", value: row.phone } : null;
}

/**
 * The register joins several numbers with "; ". Click-to-call needs one.
 *
 * Exported because the call list picks the same one: a row ticked for a run has
 * to dial the number the row is showing, and two copies of this rule would
 * eventually disagree about which of a company's three numbers that is.
 */
export function firstNumber(phone: string): string {
  return (phone.split(";")[0] ?? phone).trim();
}

/** The registered website, as a link. The DNS check lives in the row menu. */
function WebLine({ webUrl }: { webUrl: string | null }) {
  if (!webUrl) return <span className="crm-mut text-[12.5px]">-</span>;

  // `parseWebUrl` rather than the raw column: most register rows hold the
  // address without a scheme, and "www.helmes.ee" in an href is a relative
  // path - the browser would resolve it against the CRM itself.
  const link = parseWebUrl(webUrl);
  if (!link) {
    // The field holds something that is not a site (an e-mail address, a note).
    // Shown, because the operator should see what the register says, but not as
    // a link that cannot go anywhere.
    return (
      <span className="crm-mut crm-trunc block text-[12.5px]" title={webUrl}>
        {webUrl}
      </span>
    );
  }

  return (
    <a
      href={link.href}
      target="_blank"
      rel="noreferrer noopener"
      title={webUrl}
      // Above the row-covering overlay, or the link is dead.
      className="crm-trunc relative z-[1] inline-flex min-w-0 items-center gap-1.5 text-[12.5px] text-[var(--color-primary)] hover:underline"
    >
      <Globe className="size-3 shrink-0" />
      <span className="crm-trunc">{link.label}</span>
    </a>
  );
}

export function ContactCell({ row, q }: { row: ArListRow; q: string }) {
  const contact = preferredContact(row, q);
  return (
    <span className="min-w-0">
      <WebLine webUrl={row.webUrl} />
      {contact === null ? null : contact.kind === "phone" ? (
        // `relative z-[1]` for the same reason as the link above: the row's name
        // anchor stretches an ::after over the whole row and would eat the click.
        <span className="crm-prow-s crm-trunc relative z-[1] block">
          {/* Styled down to match the e-mail line rather than left on
              PhoneLink's default primary blue. Both are the same slot - the
              secondary contact under the website - and measured side by side the
              default read as two different kinds of thing (11.5px muted with an
              icon vs 12px primary without one). It is still click-to-call. */}
          <PhoneLink
            phone={firstNumber(contact.value)}
            source="ariregister"
            companyName={row.name}
            regCode={row.regCode}
            stopPropagation
            className="max-w-full gap-1.5 text-[11.5px] text-[var(--color-muted-foreground)]"
          />
        </span>
      ) : (
        <a
          href={`mailto:${contact.value}`}
          className="crm-prow-s crm-trunc relative z-[1] flex min-w-0 items-center gap-1.5 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          <Mail className="size-3 shrink-0 opacity-70" />
          <span className="crm-trunc">{contact.value}</span>
        </a>
      )}
    </span>
  );
}
